import { Link } from 'react-router-dom'
import { EtiquetaDePrioridade, EtiquetaDeRisco } from '../../../components/Etiqueta'
import { cn } from '../../../lib/cn'
import { data, dataHoraPorExtenso } from '../../../lib/datas'
import type { ResumoGmud } from '../../../types/gmud'
import { EstadoNaLista, SeloDeBloqueio, ValorOuVazio } from './marcadores'

/**
 * A mesma solicitação, no telefone.
 *
 * Cartão e não tabela rolando de lado: fiscal de contrato confere GMUD do celular, e uma tabela
 * de oito colunas em 360px de largura vira uma faixa que se arrasta com o dedo — quem lê a
 * primeira coluna nunca chega no estado, que é a informação que ele foi buscar.
 *
 * O cartão inteiro é UM link, e não uma caixa clicável com um link dentro: assim o alvo de
 * toque é o cartão todo, o leitor de tela anuncia um destino só e o "abrir em nova aba" do
 * navegador continua funcionando. Por isso nada aqui dentro pode virar botão.
 *
 * @param props a linha da listagem
 * @returns o cartão
 */
export function CartaoDeGmud({ gmud }: { gmud: ResumoGmud }) {
  const migrado = gmud.origem === 'MIGRADO_V1'

  return (
    <li>
      <Link
        to={`/gmuds/${gmud.id}`}
        className={cn(
          'block rounded-xl border p-4 shadow-cartao transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-niteroi-600',
          migrado
            ? 'border-slate-300 border-l-4 border-l-slate-400 bg-slate-100 hover:bg-slate-200/70'
            : 'border-slate-200 bg-white hover:border-niteroi-200 hover:bg-niteroi-50',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 font-semibold text-niteroi-700">
            {gmud.codigo}
            {gmud.bloqueadoParaDeploy && <SeloDeBloqueio />}
          </span>
          <time
            dateTime={gmud.criadoEm}
            title={dataHoraPorExtenso(gmud.criadoEm)}
            className="text-xs tabular-nums text-slate-500"
          >
            {data(gmud.criadoEm)}
          </time>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <EstadoNaLista gmud={gmud} />
          <EtiquetaDePrioridade prioridade={gmud.prioridade} rotulo={gmud.prioridadeRotulo} />
          <EtiquetaDeRisco risco={gmud.riscoNivel} />
        </div>

        {/* `<dl>` e não linhas soltas: sem a marcação, "Chamado" e "2026051281" viram dois
            textos sem relação para quem ouve a tela. */}
        <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-slate-500">Chamado</dt>
          <dd className="min-w-0 text-slate-800">
            <ValorOuVazio texto={gmud.chamadoNumero} />
          </dd>
          <dt className="text-slate-500">Sistema</dt>
          <dd className="min-w-0 text-slate-800">
            <ValorOuVazio texto={gmud.sistema} />
          </dd>
          <dt className="text-slate-500">Solicitante</dt>
          <dd className="min-w-0 text-slate-800">
            <ValorOuVazio texto={gmud.solicitante} />
          </dd>
        </dl>
      </Link>
    </li>
  )
}
